import { Configuration, getConfiguration } from '../../internal';
import { getHelpCenterDataProvider } from './__internal__/helpCenterDataProvider';

export interface SoftwareInfo {
  softwareId?: string;
  softwareVersion?: string;
  softwareSpecialConfig?: string;
  softwareContextId?: string;
}

const HELP_CENTER_BASE_URL = 'http://documentation.sas.com';

interface HelpCenterParams extends SoftwareInfo {
  requestor?: string;
  theme?: string;
}

/**
 * HelpCenterDataProvider is a contract of the data provider used by helpCenter
 * component. Consumers can implement it to provide custom implementation.
 */
export interface HelpCenterDataProvider {
  getSoftwareInfo?: () => Promise<Pick<SoftwareInfo, 'softwareVersion'>>;
}

/**
 * @returns a Promise which resolved to a url string.
 * The url starts with 'http://documentation.sas.com' and includes the following query parameter pairs:
 * requestor=inapp
 * softwareVersion (value can be set in the configuration file)
 * softwareSpecialConfig (value can be set in the configuration file)
 * softwareId (value can be set in the configuration file)
 * softwareContextId (value can be set in the configuration file)
 */
export const getHelpCenterUrl = async (args?: {
  /**
   * The generated url will contains a query parameter with key being "softwareVersion" and value
   * being from the configuration file. When there is no "softwareVersion" set in the configuration file,
   * consumer can use this property to provide a "softwareVersion" value if needed.
   */
  dataProvider?: HelpCenterDataProvider;
  /**
   * When set to true, the generated url will contain "theme=hc" query parameter pair
   */
  invertColors?: boolean;
  customParams?: Record<string, string | undefined>;
}): Promise<string> => {
  const { dataProvider = getHelpCenterDataProvider(), invertColors, customParams } = args ?? {};
  const config = await getConfiguration();
  const softwareInfo = getSoftwareInfoFromConfig(config);
  if (!softwareInfo.softwareVersion && dataProvider.getSoftwareInfo) {
    try {
      softwareInfo.softwareVersion = (await dataProvider.getSoftwareInfo()).softwareVersion;
    } catch {
      // Do nothing
    }
  }

  const helpParams: HelpCenterParams = {
    ...softwareInfo,
    requestor: 'inapp',
    theme: invertColors ? 'hc' : undefined, // 'hc' for high contrast
  };

  return buildHelpCenterUrl({ ...helpParams, ...customParams });
};

const buildHelpCenterUrl = (params: Record<string, string | undefined>) => {
  const helpParams: string[] = [];
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (value) {
      helpParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }

  return `${HELP_CENTER_BASE_URL}/?${helpParams.join('&')}`;
};

const getSoftwareInfoFromConfig = (config: Configuration | null): SoftwareInfo => {
  if (config === null) return {};
  const helpConfig = config.get('help');
  return {
    softwareVersion: helpConfig?.softwareVersion,
    softwareSpecialConfig: helpConfig?.softwareSpecialConfig,
    softwareId: helpConfig?.softwareId || config.get('appId'),
    softwareContextId: helpConfig?.softwareContextId || config.get('appId'),
  };
};
